import { asCentsUnrounded, upperFirstLetter } from 'utils/caseing';

/** Tick sizes the exchange uses, in centi-cents (hundredths of a cent). */
type TickSizeInCentiCents = 100 | 50 | 20 | 10 | 1;

interface PriceLevelRangeInCentiCents {
    start: number;
    end: number;
    tickSize: TickSizeInCentiCents;
}

const MAX_PRICE_IN_CENTI_CENTS = 10000;

// Mirrors common/types/price_level_structure.go (exchange-infra). Prices use integer
// centi-cents because that is the finest tick the exchange defines, so every structure
// below is exact at this precision. Ranges are ascending and share boundaries.
//
// `banded_centi_cent` is deliberately absent: its tick is a per-market parameter the
// market VO does not carry, so it degrades to the unknown-structure behaviour below.
const PRICE_LEVEL_RANGES_IN_CENTI_CENTS = {
    linear_cent: [{ start: 0, end: 10000, tickSize: 100 }],
    deci_cent: [{ start: 0, end: 10000, tickSize: 10 }],
    tapered_deci_cent: [
        { start: 0, end: 1000, tickSize: 10 },
        { start: 1000, end: 9000, tickSize: 100 },
        { start: 9000, end: 10000, tickSize: 10 },
    ],
    center_whole_edge_half_cent: [
        { start: 0, end: 1000, tickSize: 50 },
        { start: 1000, end: 9000, tickSize: 100 },
        { start: 9000, end: 10000, tickSize: 50 },
    ],
    center_whole_edge_quint_cent: [
        { start: 0, end: 1000, tickSize: 20 },
        { start: 1000, end: 9000, tickSize: 100 },
        { start: 9000, end: 10000, tickSize: 20 },
    ],
    center_half_edge_half_cent: [{ start: 0, end: 10000, tickSize: 50 }],
    center_half_edge_quint_cent: [
        { start: 0, end: 1000, tickSize: 20 },
        { start: 1000, end: 9000, tickSize: 50 },
        { start: 9000, end: 10000, tickSize: 20 },
    ],
    center_half_edge_deci_cent: [
        { start: 0, end: 1000, tickSize: 10 },
        { start: 1000, end: 9000, tickSize: 50 },
        { start: 9000, end: 10000, tickSize: 10 },
    ],
    center_quint_edge_quint_cent: [{ start: 0, end: 10000, tickSize: 20 }],
    center_quint_edge_deci_cent: [
        { start: 0, end: 1000, tickSize: 10 },
        { start: 1000, end: 9000, tickSize: 20 },
        { start: 9000, end: 10000, tickSize: 10 },
    ],
    center_centi_edge_centi_cent: [{ start: 0, end: 10000, tickSize: 1 }],
    center_deci_edge_centi_cent: [
        { start: 0, end: 100, tickSize: 1 },
        { start: 100, end: 9900, tickSize: 10 },
        { start: 9900, end: 10000, tickSize: 1 },
    ],
} satisfies Record<string, readonly PriceLevelRangeInCentiCents[]>;

/** Every tick grid the web knows how to render and validate. Derived from the table above. */
export type PriceLevelStructure = keyof typeof PRICE_LEVEL_RANGES_IN_CENTI_CENTS;

const getRanges = (structure: string): readonly PriceLevelRangeInCentiCents[] | undefined =>
    (PRICE_LEVEL_RANGES_IN_CENTI_CENTS as Record<string, readonly PriceLevelRangeInCentiCents[] | undefined>)[
        structure
    ];

const minimumTickSize = (ranges: readonly PriceLevelRangeInCentiCents[]): number =>
    Math.min(...ranges.map((range) => range.tickSize));

// Level ladders are built on first use and cached: a page touches one or two
// structures, and the centi-cent ladder alone is ~10k entries.
const actionableLevelsCache = new Map<string, readonly number[]>();
const allLevelsCache = new Map<string, readonly number[]>();
const EMPTY_LEVELS: readonly number[] = [];

/** Every price the exchange accepts an order at for this structure (endpoints excluded), in centi-cents. */
export const getActionablePriceLevelsInCentiCents = (structure: string): readonly number[] => {
    const cached = actionableLevelsCache.get(structure);
    if (cached) return cached;

    const ranges = getRanges(structure);
    if (!ranges) return EMPTY_LEVELS;

    const levels: number[] = [];
    for (const range of ranges) {
        for (let price = range.start; price <= range.end; price += range.tickSize) {
            // Ranges share boundaries, so a duplicate can only be the previous entry.
            if (price > 0 && price < MAX_PRICE_IN_CENTI_CENTS && levels[levels.length - 1] !== price) {
                levels.push(price);
            }
        }
    }
    actionableLevelsCache.set(structure, levels);
    return levels;
};

/** `getActionablePriceLevelsInCentiCents` plus the 0 and 100¢ endpoints. */
export const getAllPriceLevelsInCentiCents = (structure: string): readonly number[] => {
    const cached = allLevelsCache.get(structure);
    if (cached) return cached;

    if (!getRanges(structure)) return EMPTY_LEVELS;
    const levels = [0, ...getActionablePriceLevelsInCentiCents(structure), MAX_PRICE_IN_CENTI_CENTS];
    allLevelsCache.set(structure, levels);
    return levels;
};

/** Group native contract prices before complementing them for an opposite-side view. */
export const getCollapsedPriceLevelInCents = (priceInCents: number, structure: string): number | undefined => {
    const levels = getActionablePriceLevelsInCentiCents(structure);
    const minimum = levels[0];
    const maximum = levels.at(-1);
    if (minimum === undefined || maximum === undefined) return undefined;

    // Sub-cent markets retain an actionable boundary row instead of a 0¢ row.
    return Math.min(Math.max(Math.floor(priceInCents), minimum / 100), maximum / 100);
};

export const getMinimumPriceLevelTickSizeInCents = (structure: string): number => {
    const ranges = getRanges(structure);
    return ranges ? minimumTickSize(ranges) / 100 : 1;
};

/**
 * How many decimal places a cents input needs to express this structure's finest tick
 * (`linear_cent` → 0, tenth/fifth/half-cent ticks → 1, centi-cent ticks → 2). Unknown
 * structures allow 2 so the server stays the authority on what it accepts.
 */
export const getPriceLevelDecimalPlaces = (structure: string): number => {
    const ranges = getRanges(structure);
    if (!ranges) return 2;
    const tick = minimumTickSize(ranges);
    return tick >= 100 ? 0 : tick >= 10 ? 1 : 2;
};

export const getDirectionalActionablePriceLevelInCentiCents = (
    structure: string,
    currentPrice: number,
    direction: 'higher' | 'lower',
    minimumDistance = 0,
): number | undefined => {
    const levels = getActionablePriceLevelsInCentiCents(structure);
    if (levels.length === 0) return undefined;

    const distance = Math.max(0, minimumDistance);
    const target =
        direction === 'higher'
            ? Math.max(currentPrice + 1, currentPrice + distance)
            : Math.min(currentPrice - 1, currentPrice - distance);

    // Find the first level greater than or equal to the target. The insertion
    // point is the higher result; the item before it is the lower result.
    let low = 0;
    let high = levels.length;
    while (low < high) {
        const middle = Math.floor((low + high) / 2);
        if (levels[middle] < target) low = middle + 1;
        else high = middle;
    }

    if (direction === 'higher') return levels[Math.min(low, levels.length - 1)];

    const lowerIndex = low < levels.length && levels[low] === target ? low : low - 1;
    return levels[Math.max(0, lowerIndex)];
};

// ---- Copy ------------------------------------------------------------------------------

/** Display phrases per tick size: "…in {increments}" / "{title} pricing". */
const TICK_PHRASES: Record<TickSizeInCentiCents, { increments: string; title: string }> = {
    100: { increments: 'whole cents', title: 'Whole-cent' },
    50: { increments: 'half-cent increments', title: 'Half-cent' },
    20: { increments: 'fifth-of-cent increments', title: 'Fifth-cent' },
    10: { increments: 'tenth-of-cent increments', title: 'Tenth-cent' },
    1: { increments: 'hundredth-of-cent increments', title: 'Hundredth-cent' },
};

const formatCentiCents = (centiCents: number): string => asCentsUnrounded(centiCents / 100);

interface PriceLevelStructureShape {
    ranges: readonly PriceLevelRangeInCentiCents[];
    /** A single range across the whole 0–100¢ span. */
    isUniform: boolean;
    /** True for `linear_cent` (and any future whole-cent grid): no sub-cent copy applies. */
    isWholeCent: boolean;
    edge: PriceLevelRangeInCentiCents;
    center: PriceLevelRangeInCentiCents;
    min: string;
    max: string;
}

const describeStructure = (structure: string): PriceLevelStructureShape | undefined => {
    const ranges = getRanges(structure);
    if (!ranges) return undefined;
    const levels = getActionablePriceLevelsInCentiCents(structure);
    const isUniform = ranges.length === 1;
    return {
        ranges,
        isUniform,
        isWholeCent: isUniform && ranges[0].tickSize >= 100,
        edge: ranges[0],
        center: ranges[1] ?? ranges[0],
        min: formatCentiCents(levels[0]),
        max: formatCentiCents(levels[levels.length - 1]),
    };
};

/**
 * Validation message for a typed limit price that is off the structure's tick grid. Pass the
 * typed price on tapered structures so the message names the band the price actually fell in.
 */
export const getPriceLevelErrorText = (structure: string, priceInCents?: number): string => {
    const shape = describeStructure(structure);
    if (!shape) return "Price does not match this market's tick size";

    if (shape.isUniform) return `${upperFirstLetter(TICK_PHRASES[shape.edge.tickSize].increments)} only`;

    const centiCents = priceInCents === undefined ? undefined : Math.round(priceInCents * 100);
    const range =
        centiCents === undefined
            ? undefined
            : shape.ranges.find(({ start, end }) => centiCents >= start && centiCents <= end);
    const tick = (range ?? shape.center).tickSize;
    return `${upperFirstLetter(TICK_PHRASES[tick].increments)} only in this range`;
};

/** Accessible name for the limit-price info button. */
export const getPriceLevelStructureDetailLabel = (structure: string): string => {
    const shape = describeStructure(structure);
    if (!shape || shape.isWholeCent) return 'Limit price details';
    if (!shape.isUniform) return 'Variable pricing details';
    return `${TICK_PHRASES[shape.edge.tickSize].title} pricing details`;
};

/** Tooltip body describing where and how finely this structure accepts orders. */
export const getPriceLevelStructureTooltip = (structure: string): string => {
    const shape = describeStructure(structure);
    if (!shape || shape.isWholeCent) {
        return 'Your order will only fill at this price or better. If the market moves past your price, your order rests on the book until filled or canceled.';
    }

    if (shape.isUniform) {
        const tick = shape.edge.tickSize;
        // `deci_cent` keeps its long-standing "Sub-cent" heading.
        const heading = structure === 'deci_cent' ? 'Sub-cent pricing' : `${TICK_PHRASES[tick].title} pricing`;
        return `${heading}: accepts orders between ${shape.min} and ${shape.max} in ${TICK_PHRASES[tick].increments}.`;
    }

    const lastEdge = shape.ranges[shape.ranges.length - 1];
    return `Variable pricing: ${TICK_PHRASES[shape.edge.tickSize].increments} at the extremes (${shape.min}–${formatCentiCents(shape.edge.end)} and ${formatCentiCents(lastEdge.start)}–${shape.max}), ${TICK_PHRASES[shape.center.tickSize].increments} in between.`;
};

/**
 * One-line summary for detail tables: "0.1 cent ticks" for uniform grids, the full
 * variable-pricing sentence for tapered ones.
 */
export const getPriceLevelStructureSummary = (structure: string): string => {
    const shape = describeStructure(structure);
    if (!shape) return 'Unknown tick size';
    if (shape.isUniform) return `${shape.edge.tickSize / 100} cent ticks`;
    return getPriceLevelStructureTooltip(structure);
};

// ---- Validation -------------------------------------------------------------------------

export const isPriceLevelInCentiCents = (price: number, structure: string): boolean => {
    if (!Number.isInteger(price)) return false;

    const range = getRanges(structure)?.find(({ start, end }) => price >= start && price <= end);
    return range ? price % range.tickSize === 0 : false;
};

export const isPriceLevelInCents = (price: number, structure: string): boolean => {
    if (!Number.isFinite(price)) return false;

    // This validator is used by order-entry surfaces. If the server introduces
    // a structure before the client knows its ticks, let the server remain the
    // authority instead of blocking every order locally. Price-ladder helpers
    // intentionally keep returning an empty array for unknown structures.
    if (!getRanges(structure)) return true;

    return isPriceLevelInCentiCents(Math.round(price * 100), structure);
};
